<template>
  <div class="city-field">
    <div class="field-heading">
      <div>
        <span class="field-label">
          Город
        </span>

        <span class="field-hint">
          Выберите город, где находится компания
        </span>
      </div>

      <span
        v-if="selectedCityId"
        class="selected-badge"
      >
        Выбрано
      </span>
    </div>

    <div
      class="select-shell"
      :class="{
        active: !!selectedCityId,
        loading: isLoading
      }"
    >
      <div class="select-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 21C15.5 17.3 18 14.1 18 10.5C18 7.2 15.3 4.5 12 4.5C8.7 4.5 6 7.2 6 10.5C6 14.1 8.5 17.3 12 21Z"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />

          <circle
            cx="12"
            cy="10.5"
            r="2.3"
            stroke="currentColor"
            stroke-width="1.7"
          />
        </svg>
      </div>

      <select
        v-model="selectedCityId"
        class="city-select"
        :disabled="isLoading"
        @change="emitSelection"
      >
        <option
          disabled
          value=""
        >
          {{
            isLoading
              ? "Загружаем города..."
              : "Выберите город"
          }}
        </option>

        <option
          v-for="city in allCities"
          :key="city._id"
          :value="city._id"
        >
          {{ city.city }}
        </option>
      </select>

      <div class="select-right">
        <span
          v-if="isLoading"
          class="loader"
        ></span>

        <svg
          v-else
          class="chevron"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 8L10 12L14 8"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <transition name="status">
      <div
        v-if="selectedCity"
        class="selected-city"
      >
        <div class="selected-city-icon">
          <svg
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M5 10L8.2 13.2L15 6.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div>
          <span class="selected-city-label">
            Выбранный город
          </span>

          <strong>
            {{ selectedCity.city }}
          </strong>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch
} from "vue";

import CitiesService
  from "../api/CitiesService";

const model = defineModel({
  type: Object,
  default: null
});

const selectedCityId = ref("");
const allCities = ref([]);
const isLoading = ref(false);

const selectedCity = computed(() => {
  if (!selectedCityId.value) {
    return null;
  }

  return (
    allCities.value.find(
      city =>
        city._id ===
        selectedCityId.value
    ) || null
  );
});

async function loadCities() {
  try {
    isLoading.value = true;

    const cities =
      await CitiesService.fetchCities();

    allCities.value =
      Array.isArray(cities)
        ? cities
        : [];

    if (
      model.value?._id
    ) {
      selectedCityId.value =
        model.value._id;
    }
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки городов:",
      error
    );

    allCities.value = [];
  } finally {
    isLoading.value = false;
  }
}

function emitSelection() {
  model.value =
    selectedCity.value;
}

watch(
  () => model.value?._id,

  (id) => {
    if (
      id &&
      id !== selectedCityId.value
    ) {
      selectedCityId.value = id;
    }

    if (!id) {
      selectedCityId.value = "";
    }
  }
);

onMounted(() => {
  loadCities();
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.city-field {
  width: 100%;
}

/* =========================================================
   HEADER
========================================================= */

.field-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  margin-bottom: 9px;
}

.field-label {
  display: block;

  color: #434850;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  line-height: 1.3;
}

.field-hint {
  display: block;

  margin-top: 3px;

  color: #969ca6;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  line-height: 1.35;
}

.selected-badge {
  flex-shrink: 0;

  padding: 6px 8px;

  color: #2a8e5a;
  background: rgba(46, 171, 103, 0.09);

  border: 1px solid rgba(46, 171, 103, 0.13);
  border-radius: 9px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 8px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* =========================================================
   SELECT
========================================================= */

.select-shell {
  position: relative;

  width: 100%;
  height: 54px;

  display: flex;
  align-items: center;

  overflow: hidden;

  background: #f6f7f9;

  border: 1px solid #e0e4e9;
  border-radius: 15px;

  box-shadow:
    inset 0 1px 2px
    rgba(20, 30, 50, 0.035);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.select-shell:hover:not(.loading) {
  border-color: #cfd5dd;

  background: #f8f9fb;
}

.select-shell:focus-within {
  background: #ffffff;

  border-color: #5c8ff5;

  box-shadow:
    0 0 0 3px
    rgba(92, 143, 245, 0.13);
}

.select-shell.active {
  border-color:
    rgba(69, 137, 239, 0.35);

  background:
    linear-gradient(
      135deg,
      rgba(55, 137, 244, 0.045),
      rgba(89, 104, 231, 0.025)
    ),
    #ffffff;
}

.select-shell.active
.select-icon {
  color: #4387ee;
}

/* =========================================================
   ICON
========================================================= */

.select-icon {
  width: 50px;
  height: 100%;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #858d99;

  transition:
    color 0.2s ease;
}

.select-icon svg {
  width: 21px;
  height: 21px;
}

/* =========================================================
   NATIVE SELECT
========================================================= */

.city-select {
  min-width: 0;
  height: 100%;
  flex: 1;

  padding:
    0
    42px
    0
    0;

  appearance: none;
  -webkit-appearance: none;

  color: #252830;
  background: transparent;

  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;

  cursor: pointer;
}

.city-select:disabled {
  cursor: wait;
  opacity: 0.65;
}

.city-select option {
  color: #252830;
  background: #ffffff;

  font-family: "Eastman-Regular", sans-serif;
}

/* =========================================================
   RIGHT
========================================================= */

.select-right {
  position: absolute;
  top: 50%;
  right: 15px;

  width: 24px;
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8f96a1;

  pointer-events: none;

  transform: translateY(-50%);
}

.chevron {
  width: 18px;
  height: 18px;

  transition:
    transform 0.2s ease,
    color 0.2s ease;
}

.select-shell:focus-within
.chevron {
  color: #4a82e8;

  transform:
    rotate(180deg);
}

/* =========================================================
   LOADER
========================================================= */

.loader {
  width: 16px;
  height: 16px;

  border:
    2px solid
    rgba(67, 136, 245, 0.18);

  border-top-color: #4388f5;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}

/* =========================================================
   SELECTED CITY
========================================================= */

.selected-city {
  display: flex;
  align-items: center;
  gap: 10px;

  margin-top: 9px;

  padding:
    10px
    12px;

  background:
    rgba(53, 132, 247, 0.055);

  border:
    1px solid
    rgba(53, 132, 247, 0.1);

  border-radius: 12px;
}

.selected-city-icon {
  width: 27px;
  height: 27px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3993f2,
      #5369e8
    );

  border-radius: 9px;

  box-shadow:
    0 5px 12px
    rgba(59, 117, 221, 0.2);
}

.selected-city-icon svg {
  width: 15px;
  height: 15px;
}

.selected-city-label {
  display: block;

  color: #9299a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.selected-city strong {
  display: block;

  margin-top: 2px;

  color: #363b44;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
}

/* =========================================================
   DARK THEME
========================================================= */

html[data-theme="dark"]
.field-label {
  color: #d4d7dc;
}

html[data-theme="dark"]
.field-hint {
  color: #7f858f;
}

html[data-theme="dark"]
.selected-badge {
  color: #65d398;

  background:
    rgba(65, 193, 126, 0.1);

  border-color:
    rgba(65, 193, 126, 0.16);
}

html[data-theme="dark"]
.select-shell {
  background: #2c2d32;

  border-color: #414349;

  box-shadow:
    inset 0 1px 2px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"]
.select-shell:hover:not(.loading) {
  background: #303136;

  border-color: #4b4e55;
}

html[data-theme="dark"]
.select-shell:focus-within {
  background: #303136;

  border-color: #5c8ff5;

  box-shadow:
    0 0 0 3px
    rgba(70, 126, 229, 0.15);
}

html[data-theme="dark"]
.select-shell.active {
  background:
    linear-gradient(
      135deg,
      rgba(53, 132, 247, 0.09),
      rgba(89, 104, 231, 0.055)
    ),
    #2c2d32;

  border-color:
    rgba(89, 143, 236, 0.45);
}

html[data-theme="dark"]
.select-icon {
  color: #858b96;
}

html[data-theme="dark"]
.select-shell.active
.select-icon {
  color: #72a8ff;
}

html[data-theme="dark"]
.city-select {
  color: #f1f2f4;
}

html[data-theme="dark"]
.city-select option {
  color: #f1f2f4;

  background: #28292e;
}

html[data-theme="dark"]
.select-right {
  color: #858b95;
}

html[data-theme="dark"]
.selected-city {
  background:
    rgba(53, 132, 247, 0.08);

  border-color:
    rgba(76, 145, 244, 0.15);
}

html[data-theme="dark"]
.selected-city-label {
  color: #818791;
}

html[data-theme="dark"]
.selected-city strong {
  color: #f0f1f3;
}

/* =========================================================
   TRANSITION
========================================================= */

.status-enter-active,
.status-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;

  transform:
    translateY(-4px);
}

/* =========================================================
   ANIMATION
========================================================= */

@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .select-shell,
  .chevron,
  .status-enter-active,
  .status-leave-active {
    transition: none;
  }

  .loader {
    animation: none;
  }
}
</style>